import queryString from 'query-string';
import { IJobTitleState } from 'store/types';
import { IHealthReport } from 'types/HealthReport';
import { appConfig } from '../config/app';
import {
  IAchTransaction,
  IApiList,
  ICalculatorRules,
  ICategoryGroup,
  ICreateUpdateCalculatorRules,
  ICreateUpdateDaySegmentRequest,
  IDaySegment,
  IDaySegmentGracePeriodPolicy,
  IEmployee,
  IEmployeePayrollTip,
  IGetEmployeePayoutsResponse,
  ILedgerEntry,
  IOnboardingEmployeeList,
  IPaymentSet,
  IPayout,
  IPayoutStats,
  IPayPeriodClosure,
  IPayPeriods,
  IPayPeriodsQuery,
  IProcessOnboardingResponse,
  ISite,
  ISiteBalance,
  ISiteWithholdings,
  IUpdatePayPeriodStatusRequest,
  SiteJobSource,
} from '../types';
import { ApiClient, IApiQueryOptions } from './ApiClient';

export const Sites = {
  listJobs: (siteId: string, sources = [SiteJobSource.SITE, SiteJobSource.POS]) => ApiClient.get<IApiList<IJobTitleState>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/jobs?sources=${sources}`),
  show: (siteId: string) => ApiClient.get<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}`),
  showSiteBalance: (siteId: string) => ApiClient.get<ISiteBalance>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/balance`),
  listEmployees: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IEmployee>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/employees`, options),

  // Day Segments
  generateDaySegments: (siteId: string) => ApiClient.post<IDaySegment[]>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/day_segments`, undefined),
  createDaySegment: (siteId: string, body: ICreateUpdateDaySegmentRequest) => ApiClient.post<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments`, body),
  getDaySegments: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IDaySegment>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments`, options),
  updateDaySegment: (siteId: string, daySegmentId: string, body: ICreateUpdateDaySegmentRequest) => ApiClient.put<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`, body),
  deleteDaySegment: (siteId: string, daySegmentId: string) => ApiClient.delete<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`),
  getDaySegmentById: (siteId: string, daySegmentId: string) => ApiClient.get<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`),

  // Ledger
  listPrefundLedgerEntries: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<ILedgerEntry>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ledgers/prefund/${startDate}/${endDate}`, { ...options }),

  // Calculator rules
  createUpdateCalculatorRules: (siteId: string, body: ICreateUpdateCalculatorRules) => ApiClient.put<ICalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/calculator`, body),
  generateCalculatorRules: (siteId: string) => ApiClient.post<ICreateUpdateCalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/calculator/rules`, undefined),
  generateSalesGroups: (siteId: string) => ApiClient.post<ICategoryGroup[]>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/sales_categories_groups`, undefined),
  listCalculatorRules: (siteId: string) => ApiClient.get<ICalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/calculator`),

  // Payouts
  getPayouts: (siteId: string, options: IApiQueryOptions) => ApiClient.get<IApiList<IPayout>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts`, options),
  getPayoutsStats: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) => ApiClient.get<IPayoutStats>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts/${startDate}/${endDate}/stats`, options),
  getEmployeePayrollTips: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<IEmployeePayrollTip>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts/employee/${startDate}/${endDate}/payment_date`, options),
  getEmployeePayouts: (siteId: string, employeeId: string, options: IApiQueryOptions) => ApiClient.get<IGetEmployeePayoutsResponse>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/employee/${employeeId}/payouts`, options),
  getPayPeriod: (siteId: string, query: IPayPeriodsQuery = {}) => {
    const params = queryString.stringify(query, { sort: false });
    return ApiClient.get<IPayPeriods>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/pay_periods${params && `?${params}`}`);
  },
  updatePayPeriodStatus: (siteId: string, body: IUpdatePayPeriodStatusRequest) => ApiClient.put<IPayPeriodClosure>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/pay_periods`, body),

  // Payments
  getSitePayments: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IPaymentSet>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payments`, options),

  // ACH
  listAchTransactions: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IAchTransaction>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ach/transactions`, options),

  // Settings
  updateSiteWithholdings: (siteId: string, body: ISiteWithholdings) => ApiClient.put<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/settings/withholdings`, body),
  updateGracePeriodPolicy: (siteId: string, body: IDaySegmentGracePeriodPolicy) => ApiClient.put<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/settings/grace_period_policy`, body),
  updateBusinessDayStart: (siteId: string, businessDayStart: string) => ApiClient.put<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/settings/business_day_start`, { businessDayStart }),
  updateCreditCardFee: (siteId: string, tipCalculatorPercentFee: number) => ApiClient.put<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/settings/credit_card_fee`, { tipCalculatorPercentFee }),

  // Health Report
  generateHealthReport: (siteId: string, startDate: string, endDate: string) => ApiClient.post<IHealthReport[]>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/reports/health/${startDate}/${endDate}`, undefined),

  // Employee Onboarding
  getEmployeesList: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) => ApiClient.get<IOnboardingEmployeeList>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/pos/onboarding/${startDate}/${endDate}`, options),
  processOnboarding: (siteId: string, body: IOnboardingEmployeeList) => ApiClient.post<IProcessOnboardingResponse>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/pos/onboarding`, body),
};
